import { useState } from "react";
import { api } from "@/lib/api";
import { Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";
import { SecurityScanResult } from "@/components/tool-results";

export function WebsiteSecurityScannerPage() {
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const runCheck = async () => {
    if (!domain) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await api.tools.securityScan(domain);
      setResult(data);
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Free Website Security Scanner",
    "description": "Check your website's SSL certificate, security headers, and blacklist status. Get a security grade and actionable recommendations. No signup required.",
    "url": "https://getsent.dev/tools/website-security-scanner",
    "applicationCategory": "SecurityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What does the website security scanner check?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our scanner checks three main areas: SSL/TLS certificate validity and configuration, HTTP security headers (like Content-Security-Policy, X-Frame-Options, etc.), and whether your domain is listed on any email blacklists."
        }
      },
      {
        "@type": "Question",
        "name": "Why do security headers matter for email deliverability?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "While security headers don't directly affect email delivery, they're part of your overall domain reputation. A website with poor security practices may be associated with spam or phishing, which can indirectly impact how email providers view your domain."
        }
      },
      {
        "@type": "Question",
        "name": "What should I do if my domain is blacklisted?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "First, identify and fix the underlying issue (malware, spam, compromised account). Then, request removal from each blacklist that lists you. Most blacklists have a removal request process on their website. Monitor your domain to prevent future listings."
        }
      }
    ]
  };

  return (
    <div>
      <SEO
        title="Free Website Security Scanner - Check SSL, Headers & Blacklist Status"
        description="Scan your website for security issues. Check SSL certificates, security headers, and blacklist status. Get a security grade with actionable fixes. Free - no signup."
        canonical="/tools/website-security-scanner"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-amber-50/50 to-transparent py-16 dark:from-amber-950/20 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-amber-500/10 px-4 py-1.5 text-sm font-medium text-amber-600">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0-10.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.25-8.25-3.286zm0 13.036h.008v.008H12v-.008z" />
              </svg>
              Free Tool
            </div>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
              Website Security{" "}
              <span className="bg-gradient-to-r from-amber-500 to-orange-600 bg-clip-text text-transparent">
                Scanner
              </span>
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Check your SSL certificate, security headers, and blacklist status.
              Get a security grade with actionable recommendations.
            </p>
          </div>
        </div>
      </section>

      {/* Tool */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="rounded-2xl border bg-card p-6 shadow-lg">
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex-1">
                <label className="mb-2 block text-sm font-medium">Enter your domain</label>
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="example.com"
                  className="w-full rounded-lg border bg-background px-4 py-3 text-base focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                  onKeyDown={(e) => e.key === "Enter" && runCheck()}
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={runCheck}
                  disabled={loading || !domain}
                  className="w-full rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 px-8 py-3 font-semibold text-white shadow-lg shadow-amber-500/25 transition-all hover:shadow-xl hover:shadow-amber-500/30 disabled:opacity-50 sm:w-auto"
                >
                  {loading ? "Scanning..." : "Scan Website"}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-6 rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-red-600">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-6">
              <SecurityScanResult data={result} />
            </div>
          )}
        </div>
      </section>

      {/* What We Check */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">What We Check</h2>
          <div className="mt-6 grid gap-4">
            <div className="rounded-xl border bg-card p-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10">
                  <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                  </svg>
                </div>
                <h3 className="font-semibold">SSL/TLS Certificate</h3>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                We verify your SSL certificate is valid, not expired, and using modern encryption
                protocols. We also check the certificate chain and warn about upcoming expirations.
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
                  <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                  </svg>
                </div>
                <h3 className="font-semibold">Security Headers</h3>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                We check for important HTTP security headers like Content-Security-Policy,
                X-Frame-Options, Strict-Transport-Security, and more. These protect against
                common web attacks.
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-500/10">
                  <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </div>
                <h3 className="font-semibold">Blacklist Status</h3>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                We check multiple DNS blacklists to see if your domain or IP is listed.
                Being blacklisted can severely impact email deliverability and website reputation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Security Headers Explained */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">Important Security Headers</h2>
          <div className="mt-6 space-y-4">
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold">Content-Security-Policy (CSP)</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Controls which resources can be loaded on your page. Prevents XSS attacks
                by restricting inline scripts and untrusted sources.
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold">Strict-Transport-Security (HSTS)</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Forces browsers to only use HTTPS connections. Prevents downgrade attacks
                and cookie hijacking.
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold">X-Frame-Options</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Prevents your site from being embedded in iframes on other sites.
                Protects against clickjacking attacks.
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold">X-Content-Type-Options</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Prevents browsers from MIME-sniffing content types. Reduces exposure
                to drive-by download attacks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
          <div className="mt-6 space-y-6">
            <div>
              <h3 className="font-semibold">What does the website security scanner check?</h3>
              <p className="mt-2 text-muted-foreground">
                Our scanner checks three main areas: SSL/TLS certificate validity and configuration,
                HTTP security headers (like Content-Security-Policy, X-Frame-Options, etc.),
                and whether your domain is listed on any email blacklists.
              </p>
            </div>
            <div>
              <h3 className="font-semibold">Why do security headers matter for email deliverability?</h3>
              <p className="mt-2 text-muted-foreground">
                While security headers don't directly affect email delivery, they're part of your
                overall domain reputation. A website with poor security practices may be associated
                with spam or phishing, which can indirectly impact how email providers view your domain.
              </p>
            </div>
            <div>
              <h3 className="font-semibold">What should I do if my domain is blacklisted?</h3>
              <p className="mt-2 text-muted-foreground">
                First, identify and fix the underlying issue (malware, spam, compromised account).
                Then, request removal from each blacklist that lists you. Most blacklists have
                a removal request process on their website. Monitor your domain to prevent future listings.
              </p>
            </div>
            <div>
              <h3 className="font-semibold">How often should I scan my website?</h3>
              <p className="mt-2 text-muted-foreground">
                We recommend scanning at least monthly, or whenever you make significant changes
                to your website configuration. Regular scanning helps catch issues like expiring
                SSL certificates before they become problems.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Tools */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-center">Related Tools</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Link
              to="/tools/email-authentication-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-amber-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-amber-600">Email Authentication Checker</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Check SPF, DKIM, and DMARC all at once.
              </p>
            </Link>
            <Link
              to="/tools/dmarc-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-amber-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-amber-600">DMARC Checker</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Check your DMARC policy configuration.
              </p>
            </Link>
            <Link
              to="/guides/website-security-email-deliverability"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-amber-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-amber-600">Security & Deliverability Guide</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                How website security affects email deliverability.
              </p>
            </Link>
            <Link
              to="/guides/fixing-security-headers"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-amber-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-amber-600">Fixing Security Headers</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Step-by-step guide to add security headers.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t py-16">
        <div className="mx-auto max-w-7xl px-6 text-center lg:px-8">
          <h2 className="text-2xl font-bold sm:text-3xl">
            Want continuous security monitoring?
          </h2>
          <p className="mt-2 text-muted-foreground">
            Get alerts when your SSL expires or security issues are detected. Free for one domain.
          </p>
          <Link
            to="/register"
            className="mt-6 inline-flex rounded-full bg-gradient-to-r from-amber-500 to-orange-600 px-8 py-3 font-semibold text-white shadow-lg shadow-amber-500/25 transition-all hover:shadow-xl hover:shadow-amber-500/30"
          >
            Start free monitoring
          </Link>
        </div>
      </section>
    </div>
  );
}
